// PROTOTYPE — "Copy all" on alert/notice detail panels. Formatters, column lists, and the button
// live in this file so the feature can be deleted wholesale. To remove: delete this file +
// CopyRowButton.test.tsx, drop <CopyRowButton> from the five Alerts pages.
import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AlertCmStatus,
  AlertSubscriptionStatus,
  CdrnAlert,
  CdrnPaymentTypeLabel,
  EthocaAlert,
  formatVerifiStatusCode,
  getCdrnReasonDescription,
  VerifiStatusCodeDescriptions,
} from '../../types/alerts';
import { VerifiNotice } from '../../types/verifiNotice';
import { AlertLog, RefundLogStatus } from '../../types/autoRefund';
import { pascalCase } from '../../utils/textUtils';
import { formatAgeLabel } from '../../utils/formatAge';

dayjs.extend(utc);

// ── Formatters (mirror the server's CSV output) ────────────────────────────────────────────────

const tsvRow = (cells: string[]): string =>
  cells.map(c => String(c ?? '').replace(/[\t\r\n]+/g, ' ')).join('\t');

const fromCents = (cents: number | null | undefined): string =>
  cents == null ? '' : (cents / 100).toFixed(2);

const formatDateUtc = (value: Date | string | null | undefined): string =>
  value ? dayjs.utc(value).format('YYYY-MM-DD HH:mm:ss') : '';

const resolvedByEmail = (cmStatus: string, logs: AlertLog[] | []): string => {
  if (cmStatus !== AlertCmStatus.RESOLVED) return '';
  const newestFirst = [...logs].sort(
    (a, b) => dayjs.utc(b.created_at).valueOf() - dayjs.utc(a.created_at).valueOf(),
  );
  const resolvedLog = newestFirst.find(
    l => l.trigger_source === 'manual' && l.status === RefundLogStatus.MANUAL_SUCCESS,
  );
  return resolvedLog?.user_email ?? '';
};

// ── Per-provider columns ───────────────────────────────────────────────────────────────────────
// Order MUST match *_CSV_HEADER in apps/api/routes/alerts.ts — see cross-ref comments there.

type CopyColumn<T> = (row: T) => string;

export type CopyRowColumnSet = 'ethoca' | 'cdrn' | 'verifi-notice';

const ETHOCA_COPY_COLUMNS: CopyColumn<EthocaAlert>[] = [
  a => a.ethoca_alert_id ?? '',                                      // Alert ID
  a => pascalCase(a.cm_status),                                      // CM Status
  a => a.status ?? '',                                               // Status (Provider)
  a => pascalCase(a.subscription_status),                            // Subscription Status
  a => a.company_name ?? '',                                        // Company
  a => a.brand_name ?? '',                                          // Brand
  a => a.merchant_name ?? '',                                       // Merchant
  a => a.descriptor_name ?? '',                                     // Descriptor
  a => a.type ?? '',                                                // Type
  a => formatDateUtc(a.alert_timestamp),                            // Alert Timestamp
  a => fromCents(a.alert_amount),                                   // Amount
  a => a.alert_currency ?? '',                                      // Currency
  a => a.card_brand ?? '',                                          // Card Brand
  a => a.card_bin ?? '',                                            // Card BIN
  a => a.card_last4 ?? '',                                          // Card Last4
  a => a.auth_code ?? '',                                           // Auth Code
  a => a.transaction_id ?? '',                                      // Transaction ID
  a => a.metadata?.issuer ?? '',                                    // Issuer
  a => a.arn ?? '',                                                 // ARN
  a => a.metadata?.mcc ?? '',                                       // MCC
  a => a.metadata?.gateway ?? '',                                   // Gateway
  a => a.metadata?.gateway_id ?? '',                                // Gateway ID
  a => (a.transaction_type ? pascalCase(a.transaction_type) : ''),  // Transaction Type
  a => formatDateUtc(a.resolved_at),                               // Resolved At
  a => resolvedByEmail(a.cm_status, a.logs),                        // Resolved By
  a => (a.refunded ? pascalCase(a.refunded) : ''),                  // Refunded
  a => (a.outcome ? pascalCase(a.outcome) : ''),                    // Outcome
  a => formatDateUtc(a.transaction_timestamp),                      // Transaction Timestamp
  a => a.matched_order_url ?? '',                                   // Matched Order URL
  a => a.cm_notes ?? '',                                            // Notes
  a => a.assigned_email ?? '',                                      // Assigned To
  a => a.matched_gateway_title ?? '',                               // Matched Gateway
  a => a.google_sheet_mid ?? '',                                    // Sheets MID
  a => (a.fulfillment_status ? pascalCase(a.fulfillment_status) : ''),  // Fulfillment Status
];

const CDRN_COPY_COLUMNS: CopyColumn<CdrnAlert>[] = [
  a => a.alert_key ?? '',                                           // Alert ID
  a => pascalCase(a.cm_status),                                     // CM Status
  a => a.status ?? '',                                              // Status (Provider)
  a => pascalCase(a.subscription_status),                           // Subscription Status
  a => a.company_name ?? '',                                       // Company
  a => a.brand_name ?? '',                                         // Brand
  a => a.alert_type ?? '',                                          // Type
  a => a.merchant_name ?? '',                                      // Merchant
  a => a.descriptor_name ?? '',                                    // Descriptor
  a => a.descriptor_contact ?? '',                                 // Descriptor Contact
  a => formatDateUtc(a.network_timestamp),                         // Alert Timestamp
  a => fromCents(a.reference_transaction_amount),                  // Amount
  a => a.reference_transaction_currency ?? '',                     // Currency
  a => a.card_bin ?? '',                                           // Card BIN
  a => a.card_last4 ?? '',                                         // Card Last4
  a => a.auth_code ?? '',                                          // Auth Code
  a => a.arn ?? '',                                                // ARN
  a => a.reason_code ?? '',                                        // Reason Code
  a => getCdrnReasonDescription(a.payment_type ?? null, a.reason_code) ?? '',  // Reason Description
  a => a.status_code ?? '',                                        // Resolution Code
  a => (a.status_code ? VerifiStatusCodeDescriptions[a.status_code] ?? a.status_code : ''),  // Resolution Description
  a => a.reference_transaction_date ?? '',                        // Transaction Date
  a => formatDateUtc(a.resolved_at),                              // Resolved At
  a => resolvedByEmail(a.cm_status, a.logs),                       // Resolved By
  a => (a.age_hours != null ? formatAgeLabel(a.age_hours) : ''),   // Case Age
  a => (a.payment_type ? CdrnPaymentTypeLabel[a.payment_type] ?? a.payment_type : ''),  // Payment Type
  a => a.matched_order_url ?? '',                                  // Matched Order URL
  a => a.cm_notes ?? '',                                           // Notes
  a => a.assigned_email ?? '',                                     // Assigned To
  a => a.matched_gateway_title ?? '',                              // Matched Gateway
  a => a.google_sheet_mid ?? '',                                   // Sheets MID
  a => (a.fulfillment_status ? pascalCase(a.fulfillment_status) : ''),  // Fulfillment Status
];

const OPEN_SUBSCRIPTION_STATUSES: Set<string> = new Set([
  AlertSubscriptionStatus.ACTION_NEEDED,
  AlertSubscriptionStatus.QUEUED,
]);

const noticeResolvedAt = (n: VerifiNotice): Date | string | null =>
  OPEN_SUBSCRIPTION_STATUSES.has(n.subscription_status) ? null : (n.subscription_status_changed_at ?? null);

const noticeStatusCode = (n: VerifiNotice): string => (n.status_code ? formatVerifiStatusCode(n.status_code) : '');

const billableColumn: CopyColumn<VerifiNotice> = n => (n.billable ? 'Yes' : 'No');
const refundedExternallyColumn: CopyColumn<VerifiNotice> = n => (n.refunded_externally ? pascalCase(n.refunded_externally) : '');
const fulfillmentStatusColumn: CopyColumn<VerifiNotice> = n => (n.fulfillment_status ? pascalCase(n.fulfillment_status) : '');
const gatewayColumn: CopyColumn<VerifiNotice> = n => n.matched_gateway_title ?? '';
const midColumn: CopyColumn<VerifiNotice> = n => n.google_sheet_mid ?? '';

const VERIFI_NOTICE_COPY_COLUMNS_BEFORE_BILLABLE: CopyColumn<VerifiNotice>[] = [
  n => n.case_id ?? '',                                             // Notice ID
  n => pascalCase(n.subscription_status),                           // Subscription Status
  n => n.case_type ?? '',                                           // Case Type
  n => n.case_event ?? '',                                          // Case Event
  n => n.company_name ?? '',                                       // Company
  n => n.brand_name ?? '',                                         // Brand
  n => n.merchant_name ?? '',                                      // Merchant Name
  () => '',                                                         // Merchant Name (Verifi Portal)
  n => formatDateUtc(n.case_date),                                 // Case Date
  n => fromCents(n.transaction_amount),                            // Amount
  n => n.transaction_currency ?? '',                               // Currency
  n => n.card_bin ?? '',                                           // Card BIN
  n => n.card_last4 ?? '',                                         // Card Last4
  n => n.acquirer_bin ?? '',                                       // Acquirer BIN
  n => n.card_acceptor_id ?? '',                                   // Card Acceptor ID
  n => n.payment_descriptor ?? '',                                 // Descriptor
  n => n.metadata?.arn ?? '',                                      // ARN
  n => n.outcome ?? '',                                            // Outcome
];

const VERIFI_NOTICE_COPY_COLUMNS_AFTER_BILLABLE: CopyColumn<VerifiNotice>[] = [
  noticeStatusCode,                                                 // Status Code
  n => n.reason_code ?? '',                                        // Reason Code
  n => formatDateUtc(n.created_at),                                // Created At
  n => formatDateUtc(noticeResolvedAt(n)),                         // Resolved At
  n => (n.age_hours != null ? formatAgeLabel(n.age_hours) : ''),   // Age
  n => n.matched_order_url ?? '',                                  // Matched Order URL
  n => n.cm_notes ?? '',                                           // Notes
  n => n.assigned_email ?? '',                                     // Assigned To
];

const VERIFI_NOTICE_COPY_COLUMNS_WITH_BILLABLE: CopyColumn<VerifiNotice>[] = [
  ...VERIFI_NOTICE_COPY_COLUMNS_BEFORE_BILLABLE,
  billableColumn,
  ...VERIFI_NOTICE_COPY_COLUMNS_AFTER_BILLABLE,
  refundedExternallyColumn,
  fulfillmentStatusColumn,
  gatewayColumn,
  midColumn,
];

const VERIFI_NOTICE_COPY_COLUMNS_WITHOUT_BILLABLE: CopyColumn<VerifiNotice>[] = [
  ...VERIFI_NOTICE_COPY_COLUMNS_BEFORE_BILLABLE,
  ...VERIFI_NOTICE_COPY_COLUMNS_AFTER_BILLABLE,
  gatewayColumn,
  midColumn,
];

const verifiNoticeCopyColumns = (notice: VerifiNotice): CopyColumn<VerifiNotice>[] =>
  notice.case_type === 'RDR_NOTICE'
    ? VERIFI_NOTICE_COPY_COLUMNS_WITH_BILLABLE
    : VERIFI_NOTICE_COPY_COLUMNS_WITHOUT_BILLABLE;

// ── Button ─────────────────────────────────────────────────────────────────────────────────────

type CopyRowButtonProps =
  | { row: EthocaAlert; columnSet: 'ethoca'; title?: string; fontSize?: string | number }
  | { row: CdrnAlert; columnSet: 'cdrn'; title?: string; fontSize?: string | number }
  | { row: VerifiNotice; columnSet: 'verifi-notice'; title?: string; fontSize?: string | number };

export default function CopyRowButton(props: CopyRowButtonProps) {
  const { columnSet, title = 'Copy all', fontSize } = props;
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const text =
        columnSet === 'ethoca'
          ? tsvRow(ETHOCA_COPY_COLUMNS.map(c => c(props.row)))
          : columnSet === 'cdrn'
            ? tsvRow(CDRN_COPY_COLUMNS.map(c => c(props.row)))
            : tsvRow(verifiNoticeCopyColumns(props.row).map(c => c(props.row)));
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // navigator.clipboard rejects in insecure contexts; nothing to recover here
    }
  };

  const tooltipTitle = copied ? 'Copied!' : title;

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton
        size="small"
        onClick={handleCopy}
        aria-label={tooltipTitle}
        sx={theme => ({ p: 0.25, flexShrink: 0, fontSize: fontSize ?? theme.typography.h6.fontSize })}
      >
        {copied ? (
          <CheckIcon fontSize="inherit" sx={{ color: 'success.main' }} />
        ) : (
          <ContentCopyIcon fontSize="inherit" />
        )}
      </IconButton>
    </Tooltip>
  );
}
